// lib/supabase.js - Production-ready configuration
import { createClient } from '@supabase/supabase-js';
import logger from '../utils/logger';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

logger.info('🔍 Supabase Environment Debug:', {
  url: supabaseUrl,
  urlLength: supabaseUrl?.length,
  hasKey: !!supabaseKey,
  keyLength: supabaseKey?.length,
  keyPreview: supabaseKey?.substring(0, 30) + '...',
  keyStartsWith: supabaseKey?.startsWith('eyJ') ? 'JWT format ✅' : '❌ Wrong format'
});

if (!supabaseUrl || !supabaseKey) {
  logger.error("Supabase URL or Key is missing.");
  throw new Error("Missing Supabase configuration");
}

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true, // Changed from false to true
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    storageKey: 'sb-auth-token',
    flowType: 'pkce',
    // Production settings for better handling
    retryDelayMin: 1000,
    retryDelayMax: 30000,
    retryAttempts: 3,
  },
  global: {
    headers: {
      'X-Client-Info': 'supabase-js-web',
    },
  },
  // Add retry configuration for better production stability
  db: {
    schema: 'public',
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Enhanced error handling for auth state changes
supabase.auth.onAuthStateChange(async (event, session) => {
  logger.info('Auth state changed:', event);
  
  switch (event) {
    case 'INITIAL_SESSION':
      if (session) {
        logger.info('Found existing session');
      }
      break;
    case 'SIGNED_IN':
      logger.info('User signed in');
      break;
    case 'SIGNED_OUT':
      logger.info('User signed out');
      if (typeof window !== 'undefined') {
        localStorage.removeItem('sb-auth-token');
      }
      break;
    case 'TOKEN_REFRESHED':
      logger.info('Token refreshed');
      break;
    case 'USER_UPDATED':
      logger.info('User updated');
      break;
    case 'PASSWORD_RECOVERY':
      logger.info('Password recovery');
      break;
    default:
      logger.info('Auth event:', event);
  }
});

// Helper function with retry logic for rate limiting
export const signUpWithRetry = async (credentials, maxRetries = 3) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const { data, error } = await supabase.auth.signUp(credentials);
      
      if (error) {
        if (error.message.includes('rate limit') && attempt < maxRetries) {
          // Wait exponentially longer between retries
          const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s
          logger.info(`Rate limited, retrying in ${delay}ms... (attempt ${attempt}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          lastError = error;
          continue;
        }
        throw error;
      }
      
      return { data, error: null };
    } catch (error) {
      lastError = error;
      if (attempt === maxRetries) {
        break;
      }
    }
  }
  
  return { data: null, error: lastError };
};

// Enhanced authentication helpers
export const isAuthenticated = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      logger.error('Error checking authentication:', error);
      return false;
    }
    return !!session?.access_token;
  } catch (error) {
    logger.error('Authentication check failed:', error);
    return false;
  }
};

export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) {
      logger.error('Error getting user:', error);
      return null;
    }
    return user;
  } catch (error) {
    logger.error('Get user failed:', error);
    return null;
  }
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      logger.error('Error signing out:', error);
      return false;
    }
    return true;
  } catch (error) {
    logger.error('Sign out failed:', error);
    return false;
  }
};